import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, ShoppingBag, Check, Mail } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { useCart } from "../hooks/useCart";
import CartPill from "../components/CartPill";
import MerchEnquireModal from "../components/MerchEnquireModal";

/**
 * Product detail page for /shop/:id.
 *
 * Phase 2 scope: show product photos, description, variant picker.
 * Phase 3 will wire the "Buy" button to a Stripe Checkout session and
 * post the order to Inkthreadable after payment.
 */
export default function InkShopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [product, setProduct] = useState(null);
  const [err, setErr] = useState("");
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [previewIndex, setPreviewIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  // Transient "Added ✓" state after Add-to-cart is clicked — lets us
  // reassure the user without a modal.
  const [justAdded, setJustAdded] = useState(false);
  // "Enquire to buy" modal state — only used when the product has
  // `enquire_only=true`. Overrides the cart/Stripe flow entirely.
  const [enquireOpen, setEnquireOpen] = useState(false);
  // Admin kill-switch — when false we render the "Checkout back soon"
  // placeholder even for a fully-configured product. Fails safe to
  // false so a network hiccup can't accidentally expose a broken
  // checkout button.
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/ink-products/${id}`)
      .then((r) => {
        if (!alive) return;
        setProduct(r.data);
        setSelectedVariantId(r.data.variants?.[0]?.id || "");
      })
      .catch(() => alive && setErr("Product not found."));
    api
      .get("/settings/ink-shop-checkout")
      .then((r) => { if (alive) setCheckoutEnabled(!!r.data?.enabled); })
      .catch(() => { /* stay disabled */ });
    return () => { alive = false; };
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-[var(--brand-bg,#F5F7FA)] text-[var(--brand-ink)]">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-20 text-center">
          <p className="text-lg font-bold">{err}</p>
          <Link to="/shop" className="mt-3 inline-flex items-center gap-1 text-sm underline">
            <ArrowLeft size={14} /> Back to the shop
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[var(--brand-bg,#F5F7FA)]">
        <Header />
        <main className="grid place-items-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--brand-body)]" />
        </main>
        <Footer />
      </div>
    );
  }

  const selectedVariant =
    product.variants.find((v) => v.id === selectedVariantId) || product.variants[0];

  // Gallery: cover first, then variant mockup (if different), then gallery.
  const previews = [
    product.cover_image,
    ...(selectedVariant?.mockup && selectedVariant.mockup !== product.cover_image
      ? [selectedVariant.mockup]
      : []),
    ...(product.gallery || []),
  ].filter(Boolean);
  const activePreview = previews[Math.min(previewIndex, previews.length - 1)] || product.cover_image;

  return (
    <div className="min-h-screen bg-[var(--brand-bg,#F5F7FA)] text-[var(--brand-ink)]" data-testid="ink-shop-detail">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10">
        <div className="flex items-center justify-between">
          <Link to="/shop" className="inline-flex items-center gap-1 text-xs text-[var(--brand-body)] hover:text-[var(--brand-ink)]" data-testid="ink-shop-detail-back">
            <ArrowLeft size={12} /> Back to the shop
          </Link>
          <CartPill />
        </div>

        <div className="mt-6 grid gap-8 lg:grid-cols-2">
          <div>
            <div className="aspect-square w-full overflow-hidden rounded-md border border-white bg-white">
              {activePreview ? (
                <img src={activePreview} alt="" className="h-full w-full object-cover" data-testid="ink-shop-detail-image" />
              ) : null}
            </div>
            {previews.length > 1 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {previews.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPreviewIndex(i)}
                    className={`h-14 w-14 overflow-hidden rounded-md border-2 ${i === previewIndex ? "border-[#0A0F17]" : "border-white"}`}
                    data-testid={`ink-shop-thumb-${i}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <h1 className="font-display text-3xl font-black leading-tight sm:text-4xl">
              {product.title}
            </h1>
            <p className="mt-2 font-display text-xl font-bold text-[var(--brand-ink)]">
              £{selectedVariant.price.toFixed(2)}
            </p>
            {product.description ? (
              <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-[var(--brand-body)]">
                {product.description}
              </p>
            ) : null}

            {product.variants.length > 1 ? (
              <div className="mt-6">
                <p className="label-kicker text-[var(--brand-body)]">Options</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setSelectedVariantId(v.id)}
                      className={`rounded-md border px-3 py-2 text-sm ${v.id === selectedVariantId ? "border-[#0A0F17] bg-[#0A0F17] text-white" : "border-black/15 hover:border-[#0A0F17]"}`}
                      data-testid={`ink-shop-variant-btn-${v.id}`}
                    >
                      {v.label} · £{v.price.toFixed(2)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-8 space-y-3">
              {product.enquire_only ? (
                <>
                  <button
                    type="button"
                    onClick={() => setEnquireOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-semibold text-white hover:opacity-90"
                    data-testid="ink-shop-enquire-btn"
                  >
                    <Mail size={14} /> Enquire to buy
                  </button>
                  <p className="text-xs text-[var(--brand-body)]">
                    Not on sale online yet — send us a short enquiry and we&apos;ll reply
                    with a payment link and confirm the order.
                  </p>
                </>
              ) : (
                <>
                  <label className="flex items-center gap-2 text-sm text-[var(--brand-body)]">
                    Quantity
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
                      className="w-20 rounded-sm border border-black/15 px-2 py-1.5 text-sm"
                      data-testid="ink-shop-quantity"
                    />
                  </label>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        cart.add(product.id, selectedVariant.id, quantity);
                        setJustAdded(true);
                        window.setTimeout(() => setJustAdded(false), 1800);
                      }}
                      className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-semibold text-white hover:opacity-90"
                      data-testid="ink-shop-add-to-cart"
                    >
                      {justAdded ? <Check size={14} /> : <ShoppingBag size={14} />}
                      {justAdded ? "Added to cart" : "Add to cart"}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/cart")}
                      className="inline-flex items-center gap-1 font-display text-xs font-semibold text-[var(--brand-ink)] hover:underline"
                      data-testid="ink-shop-view-cart"
                    >
                      View cart →
                    </button>
                  </div>
                  <p className="text-xs text-[var(--brand-body)]">
                    {checkoutEnabled
                      ? "Secure payment via Stripe · printed to order by Inkthreadable · shipped from Blackburn, UK."
                      : "Checkout back soon — but the catalog is live for browsing and you can start building your basket now."}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      {enquireOpen ? (
        <MerchEnquireModal
          product={product}
          initialVariantId={selectedVariantId}
          onClose={() => setEnquireOpen(false)}
        />
      ) : null}
      <Footer />
    </div>
  );
}
